import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PaidIcon from '@mui/icons-material/Paid';
import CommentIcon from '@mui/icons-material/Comment';
import PersonIcon from '@mui/icons-material/Person';
import StadiumIcon from '@mui/icons-material/Stadium';
import AddIcon from '@mui/icons-material/Add';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import config from '../config';
import Loading from '../components/Loading';
import { authenticatedFetch } from '../utils/tokenUtils';

const GROUPS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const AdminTeams = () => {
  // Add loading state
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [players, setPlayers] = useState([]);
  const [saveWarningOpen, setSaveWarningOpen] = useState(false);
  const [group, setGroup] = useState('');
  const [registrationFeePaid, setRegistrationFeePaid] = useState(false);
  const [comment, setComment] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [expandedContactSection, setExpandedContactSection] = useState(false);
  const [expandedTeamContacts, setExpandedTeamContacts] = useState({});
  const [isRegistrationAllowed, setIsRegistrationAllowed] = useState(true);
  const [contactNameError, setContactNameError] = useState('');
  const [contactPhoneError, setContactPhoneError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const token = localStorage.getItem('token');

  // Fetch admin configs to check if registration is allowed
  const fetchAdminConfigs = async () => {
    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/admin-configs`);
      if (response.ok) {
        const data = await response.json();
        setIsRegistrationAllowed(data.isRegistrationOngoing);
      }
    } catch (error) {
      console.error('Error fetching admin configs:', error);
      // Default to allowing registration if there's an error
      setIsRegistrationAllowed(true);
    }
  };

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const response = await fetch(`${config.apiBaseUrl}/teams?year=${currentYear}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleEditClick = team => {
    setSelectedTeam(team);
    setTeamName(team.team_name);
    setManagerName(team.manager_name);
    setPlayers(team.players.map(player => player.player_name));
    setGroup(team.group || '');
    setRegistrationFeePaid(team.registration_fee_paid || false);
    setComment(team.comment || '');
    setContactName(team.contact_name || '');
    setContactPhone(team.contact_phone || '');
    setContactEmail(team.contact_email || '');
    setExpandedContactSection(true); // Always expand for mandatory fields
    setContactNameError(''); // Clear errors
    setContactPhoneError(''); // Clear errors
    setEditDialogOpen(true);
  };

  const handleDeleteClick = team => {
    setSelectedTeam(team);
    setDeleteDialogOpen(true);
  };

  const handleEditSubmit = () => {
    // Reset previous errors
    setContactNameError('');
    setContactPhoneError('');

    // Validate required fields
    let hasErrors = false;

    if (!contactName.trim()) {
      setContactNameError('Contact Name is required');
      hasErrors = true;
    }

    if (!contactPhone.trim()) {
      setContactPhoneError('Contact Phone is required');
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    setSaveWarningOpen(true);
  };

  const handleEditConfirm = async () => {
    try {
      setLoading(true);
      // If selectedTeam is null, we're adding a new team
      if (!selectedTeam) {
        const response = await fetch(`${config.apiBaseUrl}/teams`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            team_name: teamName,
            manager_name: managerName,
            players: players.filter(name => name !== ''),
            group: group || null,
            registration_fee_paid: registrationFeePaid,
            comment: comment || null,
            contact_name: contactName,
            contact_phone: contactPhone,
            contact_email: contactEmail || null,
          }),
        });
        if (response.ok) {
          fetchTeams();
          setEditDialogOpen(false);
          setSaveWarningOpen(false);
          // Reset form fields
          setTeamName('');
          setManagerName('');
          setPlayers([]);
          setGroup('');
          setRegistrationFeePaid(false);
          setComment('');
          setContactName('');
          setContactPhone('');
          setContactEmail('');
        }
      } else {
        // We're updating an existing team
        const response = await fetch(`${config.apiBaseUrl}/teams/${selectedTeam.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            team_name: teamName,
            manager_name: managerName,
            players: players.filter(name => name !== ''),
            group: group || null,
            registration_fee_paid: registrationFeePaid,
            comment: comment || null,
            contact_name: contactName,
            contact_phone: contactPhone,
            contact_email: contactEmail || null,
          }),
        });
        if (response.ok) {
          fetchTeams();
          setEditDialogOpen(false);
          setSaveWarningOpen(false);
          setSelectedTeam(null);
        }
      }
    } catch (error) {
      console.error('Error saving team:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTeam) return;
    try {
      setLoading(true);
      const response = await fetch(`${config.apiBaseUrl}/teams/${selectedTeam.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        fetchTeams();
      }
    } catch (error) {
      console.error('Error deleting team:', error);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSelectedTeam(null);
    }
  };

  const handlePlayerChange = (index, value) => {
    const newPlayers = [...players];
    newPlayers[index] = value;
    setPlayers(newPlayers);
  };

  const handleToggleContactSection = () => {
    setExpandedContactSection(!expandedContactSection);
  };

  const handleToggleTeamContact = teamId => {
    setExpandedTeamContacts(prev => ({
      ...prev,
      [teamId]: !prev[teamId],
    }));
  };

  useEffect(() => {
    fetchTeams();
    fetchAdminConfigs();
  }, [fetchTeams]);

  const filteredTeams = teams.filter(team => !filterGroup || team.group === filterGroup);

  const renderTeamCard = team => (
    <Grid item xs={12} sm={6} md={4} key={team.id}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#FFFFFF',
          boxShadow: 3,
          '&:hover': {
            boxShadow: 6,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Typography variant="h6" component="div" sx={{ color: '#000000', fontWeight: 'bold' }}>
              {team.team_name}
            </Typography>
            <Box>
              <IconButton
                onClick={() => handleEditClick(team)}
                sx={{ color: '#DAA520', mr: 1 }}
                size="small"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleDeleteClick(team)}
                sx={{ color: '#DAA520' }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <Stack spacing={2}>
            {/* Group */}
            {team.group && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ color: '#DAA520', mr: 1 }} />
                <Typography variant="body2">
                  Group:{' '}
                  <Chip
                    label={team.group}
                    size="small"
                    sx={{ bgcolor: '#DAA520', color: 'white' }}
                  />
                </Typography>
              </Box>
            )}

            {/* Manager */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ color: '#DAA520', mr: 1 }} />
              <Typography variant="body2">Manager: {team.manager_name}</Typography>
            </Box>

            {/* Stadium */}
            {team.stadium_name && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StadiumIcon sx={{ color: '#DAA520', mr: 1 }} />
                <Typography variant="body2">Stadium: {team.stadium_name}</Typography>
              </Box>
            )}

            {/* Registration Fee Status */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PaidIcon sx={{ color: '#DAA520', mr: 1 }} />
              <Typography variant="body2">
                Registration Fee:{' '}
                <Chip
                  label={team.registration_fee_paid ? 'Paid' : 'Unpaid'}
                  size="small"
                  sx={{
                    bgcolor: team.registration_fee_paid ? '#4CAF50' : '#f44336',
                    color: 'white',
                  }}
                />
              </Typography>
            </Box>

            {/* Comment */}
            {team.comment && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <CommentIcon sx={{ color: '#DAA520', mr: 1, mt: 0.5 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {team.comment}
                </Typography>
              </Box>
            )}

            {/* Contact Person - Toggleable */}
            {team.contact_name && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => handleToggleTeamContact(team.id)}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <ContactMailIcon sx={{ fontSize: '0.8rem', color: '#DAA520' }} />
                    Contact
                    <IconButton size="small" sx={{ p: 0, ml: -0.5 }}>
                      {expandedTeamContacts[team.id] ? (
                        <ExpandLessIcon sx={{ fontSize: '0.875rem' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: '0.875rem' }} />
                      )}
                    </IconButton>
                  </Typography>
                </Box>

                <Collapse in={expandedTeamContacts[team.id] || false}>
                  <Box sx={{ pl: 2.5, pt: 0.5, pb: 0.5, color: 'text.secondary' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {team.contact_name}
                    </Typography>
                    {team.contact_phone && (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {team.contact_phone}
                      </Typography>
                    )}
                    {team.contact_email && (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {team.contact_email}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </>
            )}

            {/* Players */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#DAA520', mb: 1 }}>
                Players:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {team.players &&
                  team.players.map((player, index) => (
                    <Chip
                      key={index}
                      label={player.player_name}
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        color: '#000000',
                        mb: 1,
                      }}
                    />
                  ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        p: { xs: 2, sm: 3 },
        width: '100%',
        mx: 'auto',
      }}
    >
      {/* Loading component */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
          }}
        >
          <Loading />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: '#000000',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
          }}
        >
          Teams
        </Typography>

        {/* Only show Add Team button if registration is allowed */}
        {isRegistrationAllowed && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedTeam(null);
              setTeamName('');
              setManagerName('');
              setPlayers(['']);
              setGroup('');
              setRegistrationFeePaid(false);
              setComment('');
              setContactName('');
              setContactPhone('');
              setContactEmail('');
              setExpandedContactSection(true); // Always expand for mandatory fields
              setContactNameError(''); // Clear errors
              setContactPhoneError(''); // Clear errors
              setEditDialogOpen(true);
            }}
            sx={{
              bgcolor: '#DAA520',
              color: 'white',
              '&:hover': {
                bgcolor: '#B8860B',
              },
            }}
          >
            Add Team
          </Button>
        )}
      </Box>

      {/* If registration is not allowed, show message */}
      {!isRegistrationAllowed && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'rgba(218, 165, 32, 0.1)',
            borderRadius: 1,
            border: '1px solid #DAA520',
          }}
        >
          <Typography sx={{ color: '#000000', fontWeight: 'medium' }}>
            Team registration is currently disabled. Enable it in the Admin Settings to allow adding
            new teams.
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Filter by Group</InputLabel>
          <Select
            value={filterGroup}
            onChange={e => setFilterGroup(e.target.value)}
            label="Filter by Group"
          >
            <MenuItem value="">All Groups</MenuItem>
            {GROUPS.map(group => (
              <MenuItem key={group} value={group}>
                Group {group}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredTeams.length > 0 ? (
        <Grid container spacing={3}>
          {filteredTeams.map(team => renderTeamCard(team))}
        </Grid>
      ) : (
        <Typography sx={{ color: '#000000', textAlign: 'center' }}>No teams available.</Typography>
      )}

      {/* Edit Form Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedTeam ? 'Edit Team' : 'Add Team'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Team Name"
              variant="outlined"
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              required
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Manager Name"
              variant="outlined"
              value={managerName}
              onChange={e => setManagerName(e.target.value)}
              required
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Group</InputLabel>
              <Select value={group} onChange={e => setGroup(e.target.value)} label="Group">
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {GROUPS.map(g => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={registrationFeePaid}
                  onChange={e => setRegistrationFeePaid(e.target.checked)}
                  sx={{
                    color: '#DAA520',
                    '&.Mui-checked': {
                      color: '#DAA520',
                    },
                  }}
                />
              }
              label="Registration Fee Paid"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Players
            </Typography>
            {players.map((player, index) => (
              <TextField
                key={index}
                fullWidth
                label={`Player ${index + 1}`}
                variant="outlined"
                value={player}
                onChange={e => handlePlayerChange(index, e.target.value)}
                size={isMobile ? 'small' : 'medium'}
                sx={{ mb: 2 }}
              />
            ))}

            {/* Contact Person Fields - Toggleable */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
                mt: 3,
              }}
              onClick={handleToggleContactSection}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: '#000000', display: 'flex', alignItems: 'center' }}
              >
                Contact Person Details *
              </Typography>
              <IconButton size="small">
                {expandedContactSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedContactSection}>
              <Box sx={{ pl: 1 }}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  variant="outlined"
                  value={contactName}
                  onChange={e => {
                    setContactName(e.target.value);
                    if (contactNameError) setContactNameError(''); // Clear error when typing
                  }}
                  required
                  error={!!contactNameError}
                  helperText={contactNameError}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Contact Phone"
                  variant="outlined"
                  value={contactPhone}
                  onChange={e => {
                    setContactPhone(e.target.value);
                    if (contactPhoneError) setContactPhoneError(''); // Clear error when typing
                  }}
                  required
                  error={!!contactPhoneError}
                  helperText={contactPhoneError}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Contact Email"
                  variant="outlined"
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ mb: 2 }}
                />
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} sx={{ color: '#DAA520' }}>
            {selectedTeam ? 'Save Changes' : 'Add Team'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Warning Dialog */}
      <Dialog
        open={saveWarningOpen}
        onClose={() => setSaveWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to save these changes?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveWarningOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button onClick={handleEditConfirm} sx={{ color: '#DAA520' }}>
            Confirm Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Warning Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this team?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} sx={{ color: '#DAA520' }}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTeams;
